import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AiOutlineBuild, AiOutlineHome, AiOutlineProject, AiOutlineFundProjectionScreen, AiOutlineBank, AiOutlineEnvironment } from "react-icons/ai"; 

const Homebody3 = () => {
  const [scrollY, setScrollY] = useState(0);

  const handleScroll = () => {
    setScrollY(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const bgOpacity = Math.min(1, Math.max(0, scrollY / 500));

  const categories = [
    { 
      name: "Construction", 
      text: "We bring architectural designs to life with precision and quality craftsmanship.",
      icon: <AiOutlineBuild size={40} className="text-[#1D4ED8]" /> 
    },
    { 
      name: "Architecture", 
      text: "We design functional and visually striking structures that stand the test of time.",
      icon: <AiOutlineHome size={40} className="text-[#1D4ED8]" />
    },
    { 
      name: "Engineering", 
      text: "Our expertise ensures strong, energy-efficient, and innovative engineering solutions.",
      icon: <AiOutlineProject size={40} className="text-[#1D4ED8]" />
    },
    { 
      name: "Project Management", 
      text: "Seamless execution from concept to completion with cost control and risk mitigation.",
      icon: <AiOutlineFundProjectionScreen size={40} className="text-[#1D4ED8]" />
    },
    { 
      name: "Real Estate", 
      text: "We develop modern real estate projects with long-term value for investors.",
      icon: <AiOutlineBank size={40} className="text-[#1D4ED8]" />
    },
    { 
      name: "Sustainable Design", 
      text: "Eco-friendly designs integrating energy efficiency and smart materials for a greener future.",
      icon: <AiOutlineEnvironment size={40} className="text-[#1D4ED8]" />
    }
  ];

  return (
    <div
      className="relative bg-cover bg-center bg-no-repeat text-gray-900 py-10"
      style={{
        backgroundImage:
          'url("https://images.unsplash.com/photo-1581090699533-6b6f37ef72dc?w=600&auto=format&fit=crop&q=60")',
        opacity: bgOpacity,
        transition: "opacity 0.3s ease-out",
      }}
    >
      <div className="absolute inset-0 bg-white opacity-80 z-10"></div>

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="max-w-7xl mx-auto text-center px-6 py-12 relative z-20"
      >
        <h1 className="text-4xl font-bold text-[#29313F] mb-4">
          Building the Future with Excellence
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Petriek Structures specializes in high-quality construction, engineering, and real estate solutions.
        </p>
        <a href="/services">
          <button className="bg-[#29313F] text-white py-3 px-6 rounded-xl shadow-lg hover:bg-[#1D4ED8] transition-all duration-300">
            Explore Our Services
          </button>
        </a>
      </motion.section>

      {/* Services Section with Dynamic Cards */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-6 py-12 relative z-20"
      >
        {categories.map((category, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: index * 0.3 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl p-6 flex flex-col items-center text-center"
          >
            <div className="mb-4">{category.icon}</div>
            <h3 className="text-2xl font-semibold text-[#29313F] mb-2">{category.name}</h3>
            <p className="text-gray-600">{category.text}</p>
          </motion.div>
        ))}
      </motion.section>

      {/* Call to Action Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="bg-[#29313F] text-white py-12 px-6 text-center relative z-20"
      >
        <h2 className="text-3xl font-semibold mb-4">Let's Build Your Dream Project</h2>
        <p className="text-lg mb-8">
          Contact Petriek Structures today and bring your vision to life with expert construction solutions.
        </p>
        <a href="/contactus">
          <button className="bg-white text-[#29313F] py-3 px-6 rounded-xl shadow-lg hover:bg-[#D1D5DB] hover:text-[#1D4ED8] transition-all duration-300">
            Get in Touch
          </button>
        </a>
      </motion.section>

      {/* Overlay Effect */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#1D4ED8] to-transparent opacity-30"></div>
    </div>
  );
};

export default Homebody3;
